import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { 
  ListTodo, 
  AlertTriangle, 
  Clock, 
  CheckCircle2, 
  Target,
  Activity,
  Zap,
  FileText,
  Search,
  Filter,
  X,
  ChevronDown,
  ExternalLink
} from "lucide-react";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import axios from "axios";
import CustomSelect from "../components/CustomSelect";
import AIAssistant from "../components/AIAssistant";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_COLORS = {
  HIGH: "#ef4444",
  MEDIUM: "#f59e0b",
  LOW: "#3b82f6"
};

const STATUS_COLORS = {
  ONLINE: "#10b981",
  WIP: "#f59e0b",
  TODO: "#71717a",
  "stand-by": "#8b5cf6",
  Recurring: "#6366f1"
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [options, setOptions] = useState({ systems: [], countries: [], priorities: [], statuses: [] });
  const [loading, setLoading] = useState(true);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({
    system: "",
    country: "",
    priority: "",
    status: ""
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  // Filter tasks when search or filters change
  useEffect(() => {
    let result = [...tasks];
    
    // Apply text search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(t => 
        t.info?.toLowerCase().includes(query) ||
        t.system?.toLowerCase().includes(query) ||
        t.project?.toLowerCase().includes(query) ||
        t.country?.toLowerCase().includes(query)
      );
    }
    
    // Apply filters
    if (filters.system) {
      result = result.filter(t => t.system === filters.system);
    }
    if (filters.country) {
      result = result.filter(t => t.country === filters.country);
    }
    if (filters.priority) {
      result = result.filter(t => t.priority === filters.priority);
    }
    if (filters.status) {
      result = result.filter(t => t.status === filters.status);
    }
    
    setFilteredTasks(result);
  }, [tasks, searchQuery, filters]);

  const fetchData = async () => {
    try {
      const [statsRes, tasksRes, optionsRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/tasks`),
        axios.get(`${API}/options`)
      ]);
      setStats(statsRes.data);
      setTasks(tasksRes.data);
      setFilteredTasks(tasksRes.data);
      setOptions(optionsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchQuery("");
    setFilters({
      system: "",
      country: "",
      priority: "",
      status: ""
    });
  };

  const hasActiveFilters = searchQuery.trim() || Object.values(filters).some(v => v !== "");

  const goToTask = (taskId) => {
    navigate("/tasks", { state: { highlightTask: taskId } });
  };

  // Voice command handlers
  const handleVoiceSearch = (term) => {
    setShowSearch(true);
    setSearchQuery(term);
  };

  const handleVoiceFilterPriority = (priority) => {
    setShowSearch(true);
    setFilters(prev => ({ ...prev, priority }));
  };

  const handleVoiceFilterStatus = (status) => {
    setShowSearch(true);
    setFilters(prev => ({ ...prev, status }));
  };

  const handleVoiceCreateTask = (prefillInfo) => {
    navigate("/tasks", { state: { openCreateModal: true, prefillInfo } });
  };

  const handleExportFilteredPDF = async () => {
    if (filteredTasks.length === 0) {
      toast.error("Nessun task da esportare");
      return;
    }
    
    try {
      toast.info("Generazione report PDF filtrato...");
      
      // Build query params
      const params = new URLSearchParams();
      if (filters.system) params.append('system', filters.system);
      if (filters.country) params.append('country', filters.country);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.status) params.append('status', filters.status);
      if (searchQuery.trim()) params.append('search', searchQuery.trim());
      
      const response = await axios.post(`${API}/reports/pdf/filtered?${params.toString()}`, {}, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filterHint = filters.system || filters.country || filters.priority || filters.status || "filtrato";
      link.setAttribute('download', `TaskCommand_${filterHint}_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Report PDF esportato (${filteredTasks.length} task)`);
    } catch (error) {
      toast.error("Errore nella generazione del PDF");
    }
  };

  const handleExportPDF = async () => {
    try {
      toast.info("Generazione report PDF in corso...");
      const response = await axios.get(`${API}/reports/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `TaskCommand_Report_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Report PDF scaricato!");
    } catch (error) {
      toast.error("Errore nella generazione del PDF");
    }
  };

  if (loading || !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  const priorityData = Object.entries(stats.by_priority).map(([name, value]) => ({
    name,
    value,
    color: PRIORITY_COLORS[name]
  }));

  const statusData = Object.entries(stats.by_status).map(([name, value]) => ({
    name,
    value,
    color: STATUS_COLORS[name]
  }));

  const systemData = Object.entries(stats.by_system)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(([name, value]) => ({ name, value }));

  const urgentTasks = tasks
    .filter(t => t.priority === "HIGH" && t.status === "WIP")
    .slice(0, 5);

  const dueSoonTasks = tasks
    .filter(t => t.deadline)
    .sort((a, b) => new Date(a.deadline) - new Date(b.deadline))
    .slice(0, 5);

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-sm md:text-base text-muted-foreground mt-1">Panoramica dei tuoi task</p>
        </div>
        <Button onClick={handleExportPDF} className="w-full sm:w-auto" data-testid="dashboard-pdf-btn">
          <FileText className="w-4 h-4 mr-2" />
          <span className="hidden sm:inline">Esporta </span>Report PDF
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <Card className="border-border" data-testid="total-tasks-card">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-muted-foreground">Task Totali</p>
                <p className="text-2xl md:text-3xl font-bold mt-1">{stats.total}</p>
              </div>
              <div className="p-2 md:p-3 bg-primary/10 rounded-lg">
                <ListTodo className="w-5 h-5 md:w-6 md:h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border" data-testid="high-priority-card">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-muted-foreground">Alta Priorità</p>
                <p className="text-2xl md:text-3xl font-bold mt-1 text-red-500">{stats.by_priority.HIGH}</p>
              </div>
              <div className="p-2 md:p-3 bg-red-500/10 rounded-lg">
                <AlertTriangle className="w-5 h-5 md:w-6 md:h-6 text-red-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border" data-testid="due-soon-card">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-muted-foreground">Scadenza 7gg</p>
                <p className="text-2xl md:text-3xl font-bold mt-1 text-amber-500">{stats.due_soon}</p>
              </div>
              <div className="p-2 md:p-3 bg-amber-500/10 rounded-lg">
                <Clock className="w-5 h-5 md:w-6 md:h-6 text-amber-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border" data-testid="completion-card">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-muted-foreground">Completamento</p>
                <p className="text-2xl md:text-3xl font-bold mt-1 text-emerald-500">
                  {Math.round(stats.completion_avg * 100)}%
                </p>
              </div>
              <div className="p-2 md:p-3 bg-emerald-500/10 rounded-lg">
                <CheckCircle2 className="w-5 h-5 md:w-6 md:h-6 text-emerald-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search & Filter Section */}
      <Card className="border-border" data-testid="dashboard-search">
        <CardHeader className="p-4 pb-3">
          <div className="flex items-center justify-between">
            <button 
              className="flex items-center gap-2"
              onClick={() => setShowSearch(!showSearch)}
            >
              <CardTitle className="text-base md:text-lg flex items-center gap-2">
                <Search className="w-4 h-4 md:w-5 md:h-5 text-primary" />
                Cerca Task
                {hasActiveFilters && (
                  <Badge variant="secondary" className="ml-1">
                    {filteredTasks.length}
                  </Badge>
                )}
              </CardTitle>
              <ChevronDown className={`w-4 h-4 transition-transform ${showSearch ? 'rotate-180' : ''}`} />
            </button>
            <div className="flex items-center gap-2">
              {/* AI Assistant (Voice + Chat) */}
              <AIAssistant
                onSearch={handleVoiceSearch}
                onFilterPriority={handleVoiceFilterPriority}
                onFilterStatus={handleVoiceFilterStatus}
                onClearFilters={clearFilters}
                onCreateTask={handleVoiceCreateTask}
                onExportPdf={handleExportFilteredPDF}
              />
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="h-8 px-2">
                  <X className="w-3 h-3 mr-1" />
                  Reset
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        
        {showSearch && (
          <CardContent className="p-4 pt-0 space-y-4">
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Cerca per info, sistema, progetto..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-secondary h-10"
                data-testid="dashboard-search-input"
              />
            </div>
            
            {/* Advanced Filters */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <CustomSelect
                value={filters.system || "all"}
                onValueChange={(v) => setFilters({ ...filters, system: v === "all" ? "" : v })}
                placeholder="Sistema"
                options={[
                  { value: "all", label: "Tutti i sistemi" },
                  ...options.systems.map(s => ({ value: s, label: s }))
                ]}
                triggerClassName="bg-secondary h-9 text-sm"
              />
              
              <CustomSelect
                value={filters.country || "all"}
                onValueChange={(v) => setFilters({ ...filters, country: v === "all" ? "" : v })}
                placeholder="Paese"
                options={[
                  { value: "all", label: "Tutti i paesi" },
                  ...options.countries.map(c => ({ value: c, label: c }))
                ]}
                triggerClassName="bg-secondary h-9 text-sm"
              />
              
              <CustomSelect
                value={filters.priority || "all"}
                onValueChange={(v) => setFilters({ ...filters, priority: v === "all" ? "" : v })}
                placeholder="Priorità"
                options={[
                  { value: "all", label: "Tutte le priorità" },
                  { value: "HIGH", label: "HIGH" },
                  { value: "MEDIUM", label: "MEDIUM" },
                  { value: "LOW", label: "LOW" }
                ]}
                triggerClassName="bg-secondary h-9 text-sm"
              />
              
              <CustomSelect
                value={filters.status || "all"}
                onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}
                placeholder="Status"
                options={[
                  { value: "all", label: "Tutti gli status" },
                  { value: "ONLINE", label: "ONLINE" },
                  { value: "WIP", label: "WIP" },
                  { value: "TODO", label: "TODO" },
                  { value: "stand-by", label: "stand-by" },
                  { value: "Recurring", label: "Recurring" }
                ]}
                triggerClassName="bg-secondary h-9 text-sm"
              />
            </div>
            
            {/* Search Results */}
            {hasActiveFilters && (
              <div className="border-t border-border pt-4 mt-4">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <h4 className="text-sm font-medium text-muted-foreground">
                    {filteredTasks.length} task trovati
                  </h4>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleExportFilteredPDF}
                      className="h-7 text-xs"
                      data-testid="export-filtered-pdf-btn"
                    >
                      <FileText className="w-3 h-3 mr-1" />
                      Esporta PDF
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => navigate("/tasks")}
                      className="h-7 text-xs"
                    >
                      Vai a Lista Task
                      <ExternalLink className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {filteredTasks.slice(0, 10).map((task) => (
                    <div 
                      key={task.id}
                      className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg border border-border hover:bg-secondary transition-colors cursor-pointer"
                      onClick={() => goToTask(task.id)}
                      data-testid={`search-result-${task.id}`}
                    >
                      <div className="flex-1 min-w-0 mr-3">
                        <p className="text-sm font-medium truncate">{task.info || task.system}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-[10px]">{task.system}</Badge>
                          <span className="text-[10px] text-muted-foreground">{task.country}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge 
                          variant="outline" 
                          className={`text-[10px] ${
                            task.priority === 'HIGH' ? 'bg-red-500/10 text-red-500 border-red-500/20' :
                            task.priority === 'MEDIUM' ? 'bg-amber-500/10 text-amber-500 border-amber-500/20' :
                            'bg-blue-500/10 text-blue-500 border-blue-500/20'
                          }`}
                        >
                          {task.priority}
                        </Badge>
                        <Badge 
                          variant="outline" 
                          className={`text-[10px] ${
                            task.status === 'ONLINE' ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' :
                            task.status === 'WIP' ? 'bg-amber-500/10 text-amber-500 border-amber-500/20' :
                            'bg-zinc-800 text-zinc-400 border-zinc-700'
                          }`}
                        >
                          {task.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                  {filteredTasks.length > 10 && (
                    <p className="text-xs text-muted-foreground text-center pt-2">
                      + altri {filteredTasks.length - 10} task...
                    </p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Priority Distribution */}
        <Card className="border-border" data-testid="priority-chart">
          <CardHeader className="pb-2 p-4 md:p-6">
            <CardTitle className="text-base md:text-lg flex items-center gap-2">
              <Target className="w-4 h-4 md:w-5 md:h-5 text-primary" />
              Per Priorità
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 md:p-6 pt-0">
            <div className="h-40 md:h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={priorityData}
                    cx="50%"
                    cy="50%"
                    innerRadius={30}
                    outerRadius={55}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {priorityData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap justify-center gap-3 mt-2">
              {priorityData.map((item) => (
                <div key={item.name} className="flex items-center gap-1.5 text-xs md:text-sm">
                  <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-muted-foreground">{item.name}: {item.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Status Distribution */}
        <Card className="border-border" data-testid="status-chart">
          <CardHeader className="pb-2 p-4 md:p-6">
            <CardTitle className="text-base md:text-lg flex items-center gap-2">
              <Activity className="w-4 h-4 md:w-5 md:h-5 text-primary" />
              Per Status
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 md:p-6 pt-0">
            <div className="h-40 md:h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={30}
                    outerRadius={55}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap justify-center gap-2 md:gap-3 mt-2">
              {statusData.slice(0, 4).map((item) => (
                <div key={item.name} className="flex items-center gap-1.5 text-xs md:text-sm">
                  <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-muted-foreground">{item.name}: {item.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* System Distribution */}
        <Card className="border-border md:col-span-2 lg:col-span-1" data-testid="system-chart">
          <CardHeader className="pb-2 p-4 md:p-6">
            <CardTitle className="text-base md:text-lg flex items-center gap-2">
              <Zap className="w-4 h-4 md:w-5 md:h-5 text-primary" />
              Per Sistema
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 md:p-6 pt-0">
            <div className="h-48 md:h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={systemData} layout="vertical">
                  <XAxis type="number" hide />
                  <YAxis 
                    type="category" 
                    dataKey="name" 
                    width={70}
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }}
                  />
                  <Bar dataKey="value" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Task Lists Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* Urgent Tasks */}
        <Card className="border-border" data-testid="urgent-tasks">
          <CardHeader className="p-4 md:p-6">
            <CardTitle className="text-base md:text-lg flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 md:w-5 md:h-5 text-red-500" />
              Task Urgenti (HIGH + WIP)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 md:p-6 pt-0 space-y-2 md:space-y-3">
            {urgentTasks.length === 0 ? (
              <p className="text-muted-foreground text-sm">Nessun task urgente</p>
            ) : (
              urgentTasks.map((task) => (
                <div 
                  key={task.id} 
                  className="flex items-center justify-between p-2.5 md:p-3 bg-secondary/50 rounded-lg border border-border"
                >
                  <div className="flex-1 min-w-0 mr-2">
                    <p className="font-medium text-sm md:text-base truncate">{task.info}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-[10px] md:text-xs bg-red-500/10 text-red-500 border-red-500/20">
                        {task.system}
                      </Badge>
                      <span className="text-[10px] md:text-xs text-muted-foreground">{task.country}</span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <Progress value={task.completion * 100} className="w-14 md:w-20 h-2" />
                    <span className="text-[10px] md:text-xs text-muted-foreground">{Math.round(task.completion * 100)}%</span>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Due Soon Tasks */}
        <Card className="border-border" data-testid="due-soon-tasks">
          <CardHeader className="p-4 md:p-6">
            <CardTitle className="text-base md:text-lg flex items-center gap-2">
              <Clock className="w-4 h-4 md:w-5 md:h-5 text-amber-500" />
              Prossime Scadenze
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 md:p-6 pt-0 space-y-2 md:space-y-3">
            {dueSoonTasks.length === 0 ? (
              <p className="text-muted-foreground text-sm">Nessuna scadenza imminente</p>
            ) : (
              dueSoonTasks.map((task) => {
                const deadline = new Date(task.deadline);
                const today = new Date();
                const daysLeft = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));
                const isOverdue = daysLeft < 0;
                
                return (
                  <div 
                    key={task.id} 
                    className="flex items-center justify-between p-2.5 md:p-3 bg-secondary/50 rounded-lg border border-border"
                  >
                    <div className="flex-1 min-w-0 mr-2">
                      <p className="font-medium text-sm md:text-base truncate">{task.info}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-[10px] md:text-xs">
                          {task.system}
                        </Badge>
                        <span className="text-[10px] md:text-xs text-muted-foreground">{task.country}</span>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className={`text-xs md:text-sm font-medium ${isOverdue ? 'text-red-500' : daysLeft <= 7 ? 'text-amber-500' : 'text-muted-foreground'}`}>
                        {isOverdue ? `${Math.abs(daysLeft)}gg fa` : `${daysLeft}gg`}
                      </p>
                      <p className="text-[10px] md:text-xs text-muted-foreground">
                        {deadline.toLocaleDateString('it-IT')}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
